/**
 * Booting header bidding on a publisher's page (Atlas ch.4 B1–B2).
 *
 * One entry point the publisher's tag calls. Everything it needs comes from
 * `GET /v1/sdk-config/:siteKey` — a single payload, not one request per
 * placement, because five placements with a sticky split four ways would be
 * eight round trips before the auction could even start.
 *
 * Three things run at t=0, deliberately in parallel:
 *   config fetch · CMP resolution · Prebid/GPT readiness
 * Chaining any of them adds one wait to another and delays the first bid
 * request for no reason (ch.7 D7).
 */
import { HeaderBidding } from "./HeaderBidding.js";

/** How long a cached config may be served before re-reading. The server sends
 *  `refreshAfter`; this is only the floor if it sends nothing. */
const FALLBACK_REFRESH_SECONDS = 300;
const STORAGE_KEY = "playbid.sdkconfig";

/**
 * Read the last-known-good config.
 *
 * If our API is down the page still monetises on yesterday's config rather than
 * dropping to zero (ch.4 B2). Publishers churn over one outage, so this is a
 * commercial control as much as a technical one.
 */
export function readCachedConfig(storage, now = Date.now()) {
  try {
    const raw = storage?.getItem?.(STORAGE_KEY);
    if (!raw) return null;
    const { config, savedAt } = JSON.parse(raw);
    if (!config || typeof savedAt !== "number") return null;
    const maxAgeMs = (config.refreshAfter ?? FALLBACK_REFRESH_SECONDS) * 1000;
    // Stale-but-present still beats nothing: an expired cache is returned with
    // `stale: true` so the caller can prefer a live fetch but still fall back.
    return { config, stale: now - savedAt > maxAgeMs };
  } catch {
    // A corrupt or unavailable store must never stop the page monetising.
    return null;
  }
}

export function writeCachedConfig(storage, config, now = Date.now()) {
  try {
    storage?.setItem?.(STORAGE_KEY, JSON.stringify({ config, savedAt: now }));
  } catch {
    // Private mode, quota, blocked storage — all fine. The cache is an
    // optimisation, never a dependency.
  }
}

/**
 * Fetch the boot config, falling back to cache on any failure.
 *
 * @param {object} opts
 * @param {string} opts.apiBase
 * @param {string} opts.siteKey
 * @param {typeof fetch} [opts.fetchFn]
 * @param {Storage} [opts.storage]
 */
export async function loadSdkConfig({ apiBase, siteKey, fetchFn, storage, now = () => Date.now() }) {
  // `fetch` must be called with `window` as its receiver. Defaulting a parameter
  // to the bare function and calling it standalone works in Node and in jsdom,
  // and throws "Illegal invocation" in a browser.
  const doFetch = fetchFn ?? ((u, o) => fetch(u, o));
  const cached = readCachedConfig(storage, now());
  try {
    const res = await doFetch(`${apiBase}/v1/sdk-config/${encodeURIComponent(siteKey)}`, {
      credentials: "omit",
    });
    // The route is documented to always answer 200 with an empty-but-valid body,
    // so a non-200 means something between us and the page is broken — a proxy,
    // a captive portal, an outage — and the cache is the better answer.
    if (!res?.ok) throw new Error(`sdk-config ${res?.status}`);
    const config = await res.json();
    if (!config || typeof config !== "object") throw new Error("sdk-config malformed");
    writeCachedConfig(storage, config, now());
    return { config, source: "network" };
  } catch (err) {
    if (cached) return { config: cached.config, source: cached.stale ? "cache-stale" : "cache", error: err };
    return { config: null, source: "none", error: err };
  }
}

/**
 * Boot header bidding.
 *
 * Never throws. This runs inside someone else's page: an exception here is
 * their site broken, which is a far worse outcome than no ads.
 *
 * @returns {Promise<{ran: boolean, source?: string, excluded?: object[], error?: unknown}>}
 */
export async function bootHeaderBidding({
  apiBase,
  siteKey,
  pbjs,
  googletag,
  fetchFn,
  storage,
  sendBids,
  onDiagnostic = () => {},
  win = typeof window !== "undefined" ? window : undefined,
  now = () => Date.now(),
} = {}) {
  try {
    if (!apiBase || !siteKey) return { ran: false, error: new Error("apiBase and siteKey are required") };

    const hb = new HeaderBidding({ pbjs, googletag, sendBids, onDiagnostic, now });

    // Both start now. The CMP wait and the config wait overlap rather than
    // stack — that overlap is the whole point of D7.
    const configPromise = loadSdkConfig({ apiBase, siteKey, fetchFn, storage, now });
    const consentPromise = hb.startConsent(readCachedConfig(storage, now())?.config, win);

    const { config, source, error } = await configPromise;
    if (error) onDiagnostic("config fetch failed", { source, error: String(error) });
    if (!config) return { ran: false, source, error };

    const result = await hb.run(config, consentPromise);
    return { ...result, source };
  } catch (err) {
    // Last line of defence. Nothing above should throw; if something does, the
    // publisher's page must still work.
    onDiagnostic("boot failed", err);
    return { ran: false, error: err };
  }
}
